// @ts-check
// Texto formatado do e-mail (spec/email.md seção 19.2): área editável do bloco Texto (barra flutuante) e do modo texto
// simples (barra fixa). Cada mudança passa pelo saneador RICH_TEXT 300 ms depois da última tecla; colar entra como
// texto simples, como nas notas. As variáveis aparecem como chips não editáveis com o rótulo legível e voltam a ser
// {{caminho|reserva}} ao gravar.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { sanitizeEmailHtml } from '../../../../shared/email-design/sanitize-email-html.js';
import { EMAIL_VARIABLE_PATTERN } from '../../../../shared/email-design/email-variables.js';
import { isValidDocumentLinkUrl } from '../../../../shared/email-design/email-links.js';
import { isMockEnabled } from '../../data/api-client.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { VariablePicker, variableDisplayLabel } from './variable-picker.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailVariableCatalog} EmailVariableCatalog
 * @typedef {'bold' | 'italic' | 'underline' | 'strike' | 'color' | 'align' | 'heading' | 'list' | 'numberedList' | 'quote' | 'link'
 *   | 'variable' | 'image'} RichTextTool
 */

const SANITIZE_DELAY_MILLISECONDS = 300;
export const RICH_TEXT_COLORS = Object.freeze(['#27272a', '#52525b', '#a1a1aa', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed']);
export const BLOCK_TEXT_TOOLS = /** @type {ReadonlyArray<RichTextTool>} */ ([
  'bold', 'italic', 'underline', 'strike', 'color', 'align', 'heading', 'list', 'numberedList', 'quote', 'link', 'variable',
]);
export const RICH_TEXT_MODE_TOOLS = /** @type {ReadonlyArray<RichTextTool>} */ ([
  'bold', 'italic', 'underline', 'strike', 'color', 'align', 'list', 'numberedList', 'link', 'image', 'variable',
]);

/** @param {string} text */
const escapeAttribute = (text) => text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/** @param {string} text */
const escapeText = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Saneamento do texto formatado no navegador (o modo demonstração aceita as imagens data: PNG de exemplo). @param {string} source */
export function sanitizeRichText(source) {
  return sanitizeEmailHtml(source, { profile: 'RICH_TEXT', allowDataImages: isMockEnabled() }).html;
}

/** Rótulo legível do chip: o nome do campo e, quando houver, o valor reserva. @param {string} expression @param {EmailVariableCatalog | null} catalog */
export function variableChipLabel(expression, catalog) {
  EMAIL_VARIABLE_PATTERN.lastIndex = 0;
  const match = EMAIL_VARIABLE_PATTERN.exec(expression);
  EMAIL_VARIABLE_PATTERN.lastIndex = 0;
  const path = match?.[1] ?? expression;
  const fallback = match?.[2] ?? '';
  const label = variableDisplayLabel(path, catalog);
  return fallback ? `${label} (${fallback})` : label;
}

/** @param {string} expression @param {EmailVariableCatalog | null} catalog */
export function variableChipHtml(expression, catalog) {
  return `<span class="email-variable-chip" contenteditable="false" data-expression="${escapeAttribute(expression)}" title="${escapeAttribute(expression)}">${escapeText(variableChipLabel(expression, catalog))}</span>`;
}

/**
 * HTML saneado com as variáveis dos textos trocadas por chips (os endereços dos links ficam como estão).
 * @param {string} source @param {EmailVariableCatalog | null} catalog
 */
export function toEditorHtml(source, catalog) {
  const template = document.createElement('template');
  template.innerHTML = sanitizeRichText(source || '');
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const textNodes = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) textNodes.push(/** @type {Text} */ (node));
  for (const node of textNodes) {
    const text = node.textContent ?? '';
    if (!text.includes('{{')) continue;
    const pattern = new RegExp(EMAIL_VARIABLE_PATTERN.source, 'g');
    let lastIndex = 0;
    let replaced = '';
    for (const match of text.matchAll(pattern)) {
      replaced += escapeText(text.slice(lastIndex, match.index)) + variableChipHtml(match[0], catalog);
      lastIndex = (match.index ?? 0) + match[0].length;
    }
    if (lastIndex === 0) continue;
    replaced += escapeText(text.slice(lastIndex));
    const holder = document.createElement('template');
    holder.innerHTML = replaced;
    node.replaceWith(holder.content);
  }
  // um parágrafo vazio não tem onde pôr o cursor: sem o <br>, o texto digitado ficaria fora dele
  const onlyBlock = soleEmptyBlock(template.content);
  if (onlyBlock && onlyBlock.childNodes.length === 0) onlyBlock.append(document.createElement('br'));
  return template.innerHTML;
}

const EMPTY_BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'BLOCKQUOTE']);
// marca invisível depois do chip recém-inserido: sem um texto ao lado do chip não editável, o Chrome leva o cursor
// para o começo da linha e o que se digita em seguida vai parar antes da variável
const CARET_ANCHOR = '​';

/** O único bloco do conteúdo quando ele está vazio ou só com o <br> de lugar do cursor. @param {ParentNode} root */
function soleEmptyBlock(root) {
  const nodes = [...root.childNodes].filter((node) => node.nodeType !== 3 || (node.textContent ?? '').trim() !== '');
  if (nodes.length !== 1 || nodes[0].nodeType !== 1) return null;
  const element = /** @type {HTMLElement} */ (nodes[0]);
  if (!EMPTY_BLOCK_TAGS.has(element.tagName)) return null;
  const children = [...element.childNodes];
  if (children.length === 0) return element;
  return children.length === 1 && children[0].nodeName === 'BR' ? element : null;
}

/** Conteúdo do editor de volta ao HTML do documento: chips viram {{...}} e tudo passa pelo saneador. @param {HTMLElement} editor */
export function fromEditorElement(editor) {
  const clone = /** @type {HTMLElement} */ (editor.cloneNode(true));
  for (const chip of clone.querySelectorAll('.email-variable-chip')) {
    chip.replaceWith(document.createTextNode(/** @type {HTMLElement} */ (chip).dataset.expression ?? ''));
  }
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.textContent?.includes(CARET_ANCHOR)) node.textContent = node.textContent.split(CARET_ANCHOR).join('');
  }
  // o <br> que só segura o cursor no parágrafo vazio não vira conteúdo (abrir e sair do bloco não é mudança)
  soleEmptyBlock(clone)?.replaceChildren();
  return sanitizeRichText(clone.innerHTML);
}

/** O último parágrafo (ou item de lista) do editor, onde o cursor vai quando não há seleção guardada. @param {HTMLElement} editor */
function lastTextContainer(editor) {
  /** @type {Element} */
  let container = editor;
  for (let last = container.lastElementChild; last && /^(P|H1|H2|H3|BLOCKQUOTE|UL|OL|LI)$/.test(last.tagName); last = container.lastElementChild) {
    container = last;
  }
  return /** @type {HTMLElement} */ (container);
}

/** Guarda e devolve a seleção do editor (o foco vai para os campos dos menus). */
function useSavedSelection() {
  const rangeRef = useRef(/** @type {Range | null} */ (null));
  return {
    /** @param {HTMLElement | null} editor */
    save(editor) {
      const selection = window.getSelection();
      if (editor && selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) rangeRef.current = selection.getRangeAt(0).cloneRange();
    },
    /** @param {HTMLElement | null} editor */
    restore(editor) {
      if (!editor) return;
      editor.focus({ preventScroll: true });
      const selection = window.getSelection();
      if (!selection) return;
      selection.removeAllRanges();
      if (rangeRef.current && editor.contains(rangeRef.current.startContainer)) selection.addRange(rangeRef.current);
      else {
        // sem seleção guardada, o fim do último parágrafo (não depois dele, onde o texto ficaria solto)
        const range = document.createRange();
        range.selectNodeContents(lastTextContainer(editor));
        range.collapse(false);
        selection.addRange(range);
      }
    },
  };
}

/**
 * @param {{ html: string, onChange: (html: string) => void, catalog: EmailVariableCatalog | null, tools?: ReadonlyArray<RichTextTool>,
 *   toolbar?: 'FLOATING' | 'FIXED', readOnly?: boolean, ariaLabel: string, placeholder?: string, brandColor: string,
 *   onRequestImage?: (insert: (image: { url: string, alt: string, width: number | null }) => void) => void, editorStyle?: Record<string, string>,
 *   autoFocus?: boolean, onFocusChange?: (focused: boolean) => void, className?: string }} props
 */
export function EmailRichTextEditor({
  html: value, onChange, catalog, tools = BLOCK_TEXT_TOOLS, toolbar = 'FLOATING', readOnly = false, ariaLabel, placeholder, brandColor,
  onRequestImage, editorStyle, autoFocus = false, onFocusChange, className = '',
}) {
  const editorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const lastEmittedRef = useRef(/** @type {string | null} */ (null));
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const [isFocused, setIsFocused] = useState(false);
  const [openMenu, setOpenMenu] = useState(/** @type {'color' | 'heading' | 'link' | 'variable' | 'align' | null} */ (null));
  const [linkDraft, setLinkDraft] = useState('');
  const selection = useSavedSelection();
  const anchorRefs = {
    color: useRef(/** @type {HTMLElement | null} */ (null)),
    heading: useRef(/** @type {HTMLElement | null} */ (null)),
    link: useRef(/** @type {HTMLElement | null} */ (null)),
    variable: useRef(/** @type {HTMLElement | null} */ (null)),
    align: useRef(/** @type {HTMLElement | null} */ (null)),
  };

  // o conteúdo só é reescrito quando o valor muda por fora (desfazer, outro bloco), para não perder o cursor
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value === lastEmittedRef.current) return;
    editor.innerHTML = toEditorHtml(value, catalog);
    lastEmittedRef.current = value;
  }, [value]);

  // rótulos dos chips quando o catálogo chega depois do conteúdo; só o texto dos chips muda, para a seleção guardada
  // (o seletor de variáveis carrega o catálogo com o cursor no meio do texto) continuar valendo
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !catalog) return;
    for (const chip of editor.querySelectorAll('.email-variable-chip')) {
      const label = variableChipLabel(/** @type {HTMLElement} */ (chip).dataset.expression ?? '', catalog);
      if (chip.textContent !== label) chip.textContent = label;
    }
  }, [catalog]);

  useEffect(() => {
    if (autoFocus) requestAnimationFrame(() => editorRef.current?.focus());
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const flush = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const editor = editorRef.current;
    if (!editor) return;
    const next = fromEditorElement(editor);
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    onChange(next);
  };
  const scheduleFlush = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, SANITIZE_DELAY_MILLISECONDS);
  };

  /** @param {string} command @param {string} [argument] */
  const run = (command, argument) => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;
    selection.restore(editor);
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, argument);
    scheduleFlush();
  };

  /** @param {'color' | 'heading' | 'link' | 'variable' | 'align'} menu */
  const openToolMenu = (menu) => {
    selection.save(editorRef.current);
    if (menu === 'link') {
      const anchor = window.getSelection()?.anchorNode;
      const element = anchor ? (anchor.nodeType === 1 ? /** @type {Element} */ (anchor) : anchor.parentElement) : null;
      setLinkDraft(element?.closest('a')?.getAttribute('href') ?? '');
    }
    setOpenMenu((current) => (current === menu ? null : menu));
  };

  const applyLink = () => {
    const url = linkDraft.trim();
    setOpenMenu(null);
    if (!url) {
      run('unlink');
      return;
    }
    if (!isValidDocumentLinkUrl(url, { allowMailto: true })) return;
    const editor = editorRef.current;
    selection.restore(editor);
    const current = window.getSelection();
    if (current && current.isCollapsed) {
      document.execCommand('insertHTML', false, `<a href="${escapeAttribute(url)}">${escapeText(url)}</a>`);
      scheduleFlush();
    } else run('createLink', url);
  };

  /** @param {string} expression */
  const insertVariable = (expression) => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;
    selection.restore(editor);
    const current = window.getSelection();
    if (!current || current.rangeCount === 0) return;
    // pela Range e não pelo insertHTML: o Chrome parte o parágrafo em dois ao inserir um elemento não editável
    const range = current.getRangeAt(0);
    range.deleteContents();
    const holder = document.createElement('template');
    holder.innerHTML = variableChipHtml(expression, catalog);
    const chip = /** @type {Node} */ (holder.content.firstChild);
    const anchor = document.createTextNode(CARET_ANCHOR);
    range.insertNode(anchor);
    range.insertNode(chip);
    // o cursor fica logo depois do chip, sem espaço extra (o texto seguinte encosta na variável, como no envio)
    const after = document.createRange();
    after.setStart(anchor, CARET_ANCHOR.length);
    after.collapse(true);
    current.removeAllRanges();
    current.addRange(after);
    scheduleFlush();
  };

  const requestImage = () => {
    selection.save(editorRef.current);
    onRequestImage?.((image) => {
      selection.restore(editorRef.current);
      const width = image.width ? ` width="${Math.min(552, image.width)}"` : '';
      document.execCommand('insertHTML', false, `<img src="${escapeAttribute(image.url)}" alt="${escapeAttribute(image.alt)}"${width}>`);
      scheduleFlush();
    });
  };

  const linkIsValid = !linkDraft.trim() || isValidDocumentLinkUrl(linkDraft.trim(), { allowMailto: true });
  const showToolbar = !readOnly && (toolbar === 'FIXED' || isFocused || openMenu !== null);
  /** @param {RichTextTool} tool @param {string} icon @param {() => void} action @param {any} [ref] */
  const toolButton = (tool, icon, action, ref) => (tools.includes(tool)
    ? html`<span ref=${ref} class="email-rich-text-tool"><${LightIconButton}
        icon=${icon}
        ariaLabel=${t(`emailDesign.toolbar.${tool}`)}
        onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
        onClick=${action}
      /></span>`
    : null);

  const toolbarElement = showToolbar
    ? html`<div class="email-rich-text-toolbar" data-placement=${toolbar === 'FIXED' ? 'fixed' : 'floating'} role="toolbar" aria-label=${t('emailDesign.toolbar.label')}>
        ${toolButton('bold', 'IconBold', () => run('bold'))}
        ${toolButton('italic', 'IconItalic', () => run('italic'))}
        ${toolButton('underline', 'IconUnderline', () => run('underline'))}
        ${toolButton('strike', 'IconStrikethrough', () => run('strikeThrough'))}
        ${toolButton('color', 'IconColor', () => openToolMenu('color'), anchorRefs.color)}
        ${toolButton('align', 'IconAlignLeft', () => openToolMenu('align'), anchorRefs.align)}
        ${toolButton('heading', 'IconH1', () => openToolMenu('heading'), anchorRefs.heading)}
        ${toolButton('list', 'IconList', () => run('insertUnorderedList'))}
        ${toolButton('numberedList', 'IconListNumbers', () => run('insertOrderedList'))}
        ${toolButton('quote', 'IconBlockquote', () => run('formatBlock', 'BLOCKQUOTE'))}
        ${toolButton('link', 'IconLink', () => openToolMenu('link'), anchorRefs.link)}
        ${onRequestImage ? toolButton('image', 'IconPhoto', requestImage) : null}
        ${toolButton('variable', 'IconBraces', () => openToolMenu('variable'), anchorRefs.variable)}
      </div>`
    : null;

  return html`<div class=${`email-rich-text ${className}`} data-toolbar=${toolbar} data-focused=${isFocused ? 'true' : undefined}>
    ${toolbar === 'FIXED' ? toolbarElement : null}
    <div
      ref=${editorRef}
      class="email-rich-text-area"
      contenteditable=${readOnly ? 'false' : 'true'}
      role="textbox"
      aria-multiline="true"
      aria-label=${ariaLabel}
      aria-readonly=${readOnly ? 'true' : undefined}
      data-placeholder=${placeholder}
      style=${editorStyle}
      onInput=${scheduleFlush}
      onFocus=${() => {
        setIsFocused(true);
        onFocusChange?.(true);
      }}
      onBlur=${(/** @type {FocusEvent} */ event) => {
        selection.save(editorRef.current);
        flush();
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (next && /** @type {HTMLElement} */ (event.currentTarget).parentElement?.contains(next)) return;
        setIsFocused(false);
        onFocusChange?.(false);
      }}
      onKeyUp=${() => selection.save(editorRef.current)}
      onMouseUp=${() => selection.save(editorRef.current)}
      onPaste=${(/** @type {ClipboardEvent} */ event) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') ?? '';
        document.execCommand('insertText', false, text);
        scheduleFlush();
      }}
      onDrop=${(/** @type {DragEvent} */ event) => {
        // soltar arquivo ou HTML de fora não entra no texto
        event.preventDefault();
      }}
    ></div>
    ${toolbar === 'FLOATING' ? toolbarElement : null}
    <${Dropdown} anchorRef=${anchorRefs.color} isOpen=${openMenu === 'color'} onClose=${() => setOpenMenu(null)} width=${212} role="dialog">
      <div class="email-color-palette" role="group" aria-label=${t('emailDesign.toolbar.color')}>
        <button type="button" class="email-color-swatch" data-brand="true" style=${{ background: brandColor }}
          title=${t('emailDesign.properties.brandColor')} aria-label=${t('emailDesign.properties.brandColor')}
          onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
          onClick=${() => { setOpenMenu(null); run('foreColor', brandColor); }}></button>
        ${RICH_TEXT_COLORS.map((color) => html`<button type="button" class="email-color-swatch" style=${{ background: color }} title=${color} aria-label=${color}
          onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
          onClick=${() => { setOpenMenu(null); run('foreColor', color); }}></button>`)}
      </div>
    <//>
    <${Dropdown} anchorRef=${anchorRefs.align} isOpen=${openMenu === 'align'} onClose=${() => setOpenMenu(null)} width=${132} role="menu">
      <div class="email-rich-text-choice">
        ${[['justifyLeft', 'IconAlignLeft', 'left'], ['justifyCenter', 'IconAlignCenter', 'center'], ['justifyRight', 'IconAlignRight', 'right']].map(([command, icon, key]) => html`
          <${LightIconButton} icon=${icon} ariaLabel=${t(`emailDesign.align.${key}`)}
            onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
            onClick=${() => { setOpenMenu(null); run(command); }} />`)}
      </div>
    <//>
    <${Dropdown} anchorRef=${anchorRefs.heading} isOpen=${openMenu === 'heading'} onClose=${() => setOpenMenu(null)} width=${180} role="menu">
      <div class="email-rich-text-menu">
        ${[['P', 'paragraph'], ['H1', 'heading1'], ['H2', 'heading2'], ['H3', 'heading3']].map(([tag, key]) => html`
          <button type="button" class="email-rich-text-menu-item" data-tag=${tag}
            onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
            onClick=${() => { setOpenMenu(null); run('formatBlock', tag); }}>${t(`emailDesign.toolbar.${key}`)}</button>`)}
      </div>
    <//>
    <${Dropdown} anchorRef=${anchorRefs.link} isOpen=${openMenu === 'link'} onClose=${() => setOpenMenu(null)} width=${300} role="dialog">
      <form class="email-link-form" onSubmit=${(/** @type {Event} */ event) => { event.preventDefault(); applyLink(); }}>
        <label class="input-label" for="email-link-url">${t('emailDesign.properties.link')}</label>
        <input id="email-link-url" class="text-input" value=${linkDraft} placeholder="https://" data-error=${linkIsValid ? undefined : 'true'}
          ref=${(/** @type {HTMLInputElement | null} */ element) => element && !element.dataset.focused && (element.dataset.focused = 'true', requestAnimationFrame(() => element.focus()))}
          onInput=${(/** @type {Event} */ event) => setLinkDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        <span class="input-hint" data-danger=${linkIsValid ? undefined : 'true'}>${linkIsValid ? t('emailDesign.properties.linkHint') : t('emailDesign.properties.linkInvalid')}</span>
        <div class="email-link-form-actions">
          ${linkDraft ? html`<${Button} title=${t('emailDesign.properties.removeLink')} variant="secondary" size="small" onClick=${() => { setLinkDraft(''); setOpenMenu(null); run('unlink'); }} />` : null}
          <${Button} title=${t('emailDesign.properties.applyLink')} size="small" accent="blue" type="submit" disabled=${!linkIsValid} />
        </div>
      </form>
    <//>
    <${VariablePicker} anchorRef=${anchorRefs.variable} isOpen=${openMenu === 'variable'} onClose=${() => setOpenMenu(null)} catalog=${catalog}
      excludePaths=${['unsubscribeUrl']} onSelect=${(/** @type {string} */ expression) => insertVariable(expression)} />
  </div>`;
}
